import React, { useState, useEffect } from 'react';
import { X, ShoppingCart, Minus, Plus } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useRegion } from '../../context/RegionContext';
import StarRating from '../common/StarRating';
import Button from '../common/Button';

const ProductQuickView = ({ product, isOpen, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addToCart } = useCart();
  const { formatPrice } = useRegion();
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      
      // Scroll back to saved position
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !product) return null;

  const handleAddToCart = () => {
    addToCart(product, quantity);
    onClose();
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto animate-scale-in shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors duration-300 shadow-lg z-10"
          >
            <X className="w-6 h-6 text-dark-700" />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
            {/* Images */}
            <div>
              <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 mb-4">
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Thumbnail Images */}
              {product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                        selectedImage === index 
                          ? 'border-primary-600 scale-105' 
                          : 'border-gray-200 hover:border-primary-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div>
              <div className="mb-4">
                <StarRating rating={product.rating} reviews={product.reviews} />
              </div>

              <h2 className="text-3xl font-display font-bold text-dark-800 mb-4">
                {product.name}
              </h2>

              <div className="flex items-baseline gap-3 mb-6">
                <p className="text-4xl font-display font-bold text-primary-700">
                  {formatPrice(product.priceUSD, product.priceCAD)}
                </p>
                {product.inStock ? (
                  <span className="text-sm text-accent-600 font-semibold">In Stock</span>
                ) : (
                  <span className="text-sm text-red-600 font-semibold">Out of Stock</span>
                )}
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                {product.description}
              </p>

              {/* Product Attributes */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Style</p>
                  <p className="font-semibold text-dark-800">{product.style}</p>
                </div>
                <div className="p-3 bg-primary-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Material</p>
                  <p className="font-semibold text-dark-800">{product.material}</p>
                </div>
                <div className="p-3 bg-primary-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Room</p>
                  <p className="font-semibold text-dark-800">{product.room}</p>
                </div>
                <div className="p-3 bg-primary-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Category</p>
                  <p className="font-semibold text-dark-800 capitalize">{product.category.replace('-', ' ')}</p>
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-dark-800 mb-2">
                  Quantity
                </label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border-2 border-gray-300 rounded-lg">
                    <button
                      onClick={decrementQuantity}
                      className="p-3 hover:bg-gray-100 transition-colors duration-300"
                      disabled={quantity <= 1}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-6 font-semibold text-lg">{quantity}</span>
                    <button
                      onClick={incrementQuantity}
                      className="p-3 hover:bg-gray-100 transition-colors duration-300"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleAddToCart}
                  disabled={!product.inStock}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Add to Cart
                </Button>
                {/* <button className="p-3 border-2 border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 transition-all duration-300">
                  <Heart className="w-6 h-6 text-red-500" />
                </button> */}
              </div>

              {/* Additional Info */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <ul className="space-y-2 text-sm text-gray-600">
                  {/* <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-accent-600 rounded-full"></span>
                    Free shipping on orders over $500
                  </li> */}
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-accent-600 rounded-full"></span>
                    30-day return policy
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-accent-600 rounded-full"></span>
                    1-year warranty included
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductQuickView;
